import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useSports } from '../../../context/SportsProvider';
import { useCountryDetailScroll } from '../../hooks/useCountryDetailScroll';
import { useSubTabsStuck } from '../../hooks/useSubTabsStuck';
import { useSquadToggleRemount } from './hooks/useSquadToggleRemount';
import CountryHeader from '../../components/CountryHeader';
import { COUNTRY_VIEWS, COUNTRY_VIEW_LABELS } from './constants';
import {
  addCompetitionFavorite,
  fetchCompetitionCountries,
  removeCompetitionFavorite,
} from '../../services';
import { setWCCountries, toggleWCCountryFavorite } from '../../reducer';
import Statistics from './views/Statistics';
import Squad from './views/Squad';
import Matches from './views/Matches';
import Classification from './views/Classification';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { TAB_PARAM, FIXTURES_TAB } from 'containers/Mundial2026/utils/tabs';
import styles from './WCCountryDetail.module.scss';
import WCBackground from 'containers/Mundial2026/components/WCBackground';
import { useMundialMetrics } from 'containers/Mundial2026/hooks/useMundialMetrics';
import { INTERACTION_TYPES } from 'containers/Mundial2026/hooks/useMundialMetrics/constants';
import { useMundialScrollTracker } from 'containers/Mundial2026/hooks/useMundialScrollTracker';

const COUNTRY_ID_PARAM = 'countryId';
const SUB_TAB_PARAM = 'subTab';
const VALID_VIEWS = Object.values(COUNTRY_VIEWS);
const TAB_LABEL_MAX_LENGTH = 22;

const WCCountryDetail = () => {
  const { translationMapper } = useTranslation();
  const viewLabels = useMemo(() => translationMapper(COUNTRY_VIEW_LABELS), [
    translationMapper,
  ]);
  const dispatch = useDispatch();
  const translations = useSelector(state => state.translations);
  const assets = useSelector(state => state.asset);
  const wcCountries = useSelector(state => state.wcCountries?.countries || []);
  const wcCountriesLoaded = useSelector(state => state.wcCountries?.loaded || false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { countries, loadCountry } = useSports();
  const [favoriteOverride, setFavoriteOverride] = useState(null);
  const [showFullStats, setShowFullStats] = useState(false);
  const countryId = searchParams.get(COUNTRY_ID_PARAM);

  useEffect(
    () => {
      loadCountry();
    },
    [loadCountry, countryId],
  );

  useEffect(
    () => {
      if (wcCountriesLoaded) return;

      let cancelled = false;
      fetchCompetitionCountries()
        .then(countries => {
          if (!cancelled && countries.length > 0) {
            dispatch(setWCCountries(countries));
          }
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('Failed to load Mundial 2026 teams', error);
        });

      return () => {
        cancelled = true;
      };
    },
    [dispatch, wcCountriesLoaded],
  );

  useEffect(() => {
    const tabpanel = document.querySelector(
      '[data-role="mundial2026-tabpanel"]',
    );
    if (tabpanel) tabpanel.setAttribute('data-countries-active', '');
    return () => {
      if (tabpanel) tabpanel.removeAttribute('data-countries-active');
    };
  }, []);

  const tabParam = searchParams.get(SUB_TAB_PARAM);
  const activeTab = VALID_VIEWS.includes(tabParam)
    ? tabParam
    : COUNTRY_VIEWS.STATISTICS;

  const {
    showFullSquad,
    isSquadHidden,
    isSquadMounted,
    handleSquadToggleChange,
    resetSquadToggle,
  } = useSquadToggleRemount();

  const {
    detailRef,
    viewRef,
    viewScrolled,
    viewAtBottom,
    resetScroll,
  } = useCountryDetailScroll(activeTab);

  const { wrapperRef, sentinelRef, topLayerRef, isStuck } = useSubTabsStuck({
    scopeRef: detailRef,
  });

  const handleTabChange = useCallback(
    tab => {
      
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set(SUB_TAB_PARAM, tab);
          return next;
        },
        { replace: false },
      );

      resetSquadToggle()
      setShowFullStats(false)
    },
    [setSearchParams],
  );

  useEffect(
    () => {
      resetSquadToggle();
      setShowFullStats(false);
    },
    [countryId, resetSquadToggle],
  );

  const { isLoading, isError, data } = countries || {};
  const teamCard = data?.teamCard;
  const currentCountry = useMemo(
    () => wcCountries.find(country => country.code === countryId),
    [wcCountries, countryId],
  );
  const isFavorite =
    favoriteOverride ??
    currentCountry?.favorite ??
    Boolean(teamCard?.team?.isFavorite);

  const { trackSelectContent, trackViewItem } = useMundialMetrics();
  const countryName = teamCard?.team?.name || '';

  const handleFullStatsToggleChange = useCallback(value => {
    setShowFullStats(value);
  }, []);

  useEffect(
    () => {
      resetScroll();
    },
    [activeTab, resetScroll],
  );

  const handleMatchClick = useCallback(
    matchId => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set(TAB_PARAM, FIXTURES_TAB);
          next.set('matchId', matchId);
          next.set('from', 'country');
          const countryId = prev.get(COUNTRY_ID_PARAM);
          if (countryId) {
            next.set('returnCountryId', countryId);
          }
          next.delete(SUB_TAB_PARAM);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const handleBack = useCallback(
    () => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete(COUNTRY_ID_PARAM);
          next.delete(SUB_TAB_PARAM);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useMundialScrollTracker({
    pageTitleSegments: ['paises', countryName],
    contentSubsection: 'detalle pais',
    moduloName: countryName,
  });

  useEffect(
    () => {
      setFavoriteOverride(null);
    },
    [countryId],
  );

  const handleToggleFavorite = useCallback(
    async () => {
      if (!currentCountry?.id) return;

      try {
        if (isFavorite) {
          await removeCompetitionFavorite(currentCountry.id);
        } else {
          await addCompetitionFavorite(currentCountry.id);
        }

        dispatch(toggleWCCountryFavorite(currentCountry.id));
        setFavoriteOverride(!isFavorite);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update Mundial 2026 favorite', error);
      }
    },
    [dispatch, isFavorite, countryId, currentCountry],
  );

  const detailClass = classNames(styles['wc-country-detail'], {
    [styles['wc-country-detail--scrolled']]: viewScrolled,
  });

  const viewClass = classNames(styles['wc-country-detail__view'], {
    [styles['wc-country-detail__view--at-bottom']]: viewAtBottom,
  });

  if (isError) {
    return (
      <div ref={detailRef} className={detailClass}>
        <p className={styles['wc-country-detail__error']}>
          No se pudo cargar el país
        </p>
      </div>
    );
  }

  return (
    <WCBackground>
      <div
        ref={detailRef}
        className={detailClass}
        data-role="wc-country-detail"
        data-scrolled={viewScrolled ? '' : undefined}
        data-squad-hidden={isSquadHidden ? '' : undefined}
      >
        {!isLoading && teamCard && (
          <CountryHeader
            assets={assets}
            team={teamCard.team}
            recentResults={teamCard.recentResults}
            featuredPlayers={teamCard.featuredPlayers}
            summary={teamCard.summary}
            isFavorite={isFavorite}
            onBack={handleBack}
            onToggleFavorite={handleToggleFavorite}
            topLayerRef={topLayerRef}
          />
        )}
        <div
          ref={sentinelRef}
          className={styles['wc-country-detail__sticky-sentinel']}
          aria-hidden="true"
        />
        <div
          ref={wrapperRef}
          className={styles['wc-country-detail__tabs-wrapper']}
          data-stuck={isStuck ? '' : undefined}
        >
          <div className={styles['wc-country-detail__tabs']} role="tablist">
            {VALID_VIEWS.map(view => (
              <button
                key={view}
                type="button"
                role="tab"
                aria-selected={activeTab === view}
                className={classNames(styles['wc-country-detail__tab'], {
                  [styles['wc-country-detail__tab--active']]:
                    activeTab === view,
                })}
                onClick={() => {
                  trackSelectContent({
                    pageTitleSegments: [
                      'paises',
                      countryName,
                      viewLabels[view] || view,
                    ],
                    pageTitle: viewLabels[view] || view,
                    contentSubsection: 'detalle pais',
                    moduloName: countryName,
                    interactionType: INTERACTION_TYPES.MAIN_TABS,
                  });
                  handleTabChange(view);
                }}
              >
                {renderMaxLengthText(viewLabels[view], TAB_LABEL_MAX_LENGTH)}
              </button>
            ))}
          </div>
        </div>
        <div
          ref={viewRef}
          className={viewClass}
          data-role="country-detail-view"
        >
          {activeTab === COUNTRY_VIEWS.STATISTICS && (
            <Statistics
              statistics={data?.statistics}
              showFullStats={showFullStats}
              onToggleChange={handleFullStatsToggleChange}
            />
          )}
          {activeTab === COUNTRY_VIEWS.SQUAD && isSquadMounted && (
            <Squad
              showFullSquad={showFullSquad}
              onToggleChange={handleSquadToggleChange}
            />
          )}
          {activeTab === COUNTRY_VIEWS.MATCHES && (
            <Matches
              matches={data?.matchesTeams?.matches}
              selectedTeamId={countryId}
              onMatchClick={handleMatchClick}
            />
          )}
          {activeTab === COUNTRY_VIEWS.CLASSIFICATION && <Classification />}
        </div>
      </div>
    </WCBackground>
  );
};

export default WCCountryDetail;
